import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Search, Loader2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import MultiImageUpload from "@/components/MultiImageUpload";
import AlbumsEditor from "@/components/AlbumsEditor";

/**
 * Generic CRUD Admin page for simple resources.
 * fields: [{ name, label, type, options?, required?, textarea?, help? }]
 * columns: [{ name, label, render?(row) }]
 */
export default function AdminCrud({ title, resource, fields, columns, initialItem = {}, statusOptions }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [resourceOptions, setResourceOptions] = useState({});

  // Load remote options for any resource_select fields
  useEffect(() => {
    fields.filter((f) => f.type === "resource_select").forEach((f) => {
      api.get(`/${f.resource}`).then((r) => {
        setResourceOptions((prev) => ({ ...prev, [f.name]: r.data }));
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const finalStatusOptions = statusOptions || [
    { value: "published", label: "Publicado" },
    { value: "hidden", label: "Oculto" },
    { value: "under_construction", label: "Em construção" },
  ];
  const statusLabel = (v) => finalStatusOptions.find((o) => o.value === v)?.label || v;
  const statusChipCls = (v) => (
    v === "published" ? "bg-emerald-500/15 text-emerald-300" :
    v === "coming_soon" ? "bg-sky-500/15 text-sky-300" :
    v === "hidden" ? "bg-neutral-500/15 text-neutral-300" :
    "bg-amber-500/15 text-amber-300"
  );

  const load = () => {
    setLoading(true);
    api.get(`/${resource}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [resource]);

  const openNew = () => setEditing({ ...initialItem, status: "published" });
  const openEdit = (row) => setEditing({ ...row });
  const close = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload.id) {
        await api.put(`/admin/${resource}/${payload.id}`, payload);
        toast.success("Atualizado com sucesso");
      } else {
        await api.post(`/admin/${resource}`, payload);
        toast.success("Criado com sucesso");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Excluir "${row.name || row.title || row.question || row.label}"?`)) return;
    try {
      await api.delete(`/admin/${resource}/${row.id}`);
      toast.success("Excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const filtered = q ? items.filter((i) => JSON.stringify(i).toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[color:var(--marupiara-gold)]">{title}</h1>
          <p className="text-sm text-white/70 mt-1">{items.length} registro(s)</p>
        </div>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/65" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="pl-9 pr-3 py-2 bg-[color:var(--marupiara-charcoal)] border border-white/10 text-sm text-white focus:border-[color:var(--marupiara-gold)] focus:outline-none" />
          </div>
          <button onClick={openNew} data-testid={`admin-${resource}-new`} className="btn-gold text-sm py-2 px-4">
            <Plus size={14} /> Novo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60"><Loader2 size={16} className="animate-spin" /> Carregando…</div>
      ) : (
        <div className="bg-[color:var(--marupiara-charcoal)] border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-left text-white/60 uppercase text-xs">
              <tr>
                {columns.map((c) => <th key={c.name} className="px-4 py-3 font-medium tracking-wider">{c.label}</th>)}
                <th className="px-4 py-3 font-medium tracking-wider">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`admin-${resource}-row-${row.id}`}>
                  {columns.map((c) => (
                    <td key={c.name} className="px-4 py-3 text-white/85">
                      {c.render ? c.render(row) : String(row[c.name] ?? "")}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase px-2 py-1 tracking-wider ${statusChipCls(row.status)}`}>
                      {statusLabel(row.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`admin-${resource}-edit-${row.id}`} onClick={() => openEdit(row)} className="p-2 text-white/70 hover:text-[color:var(--marupiara-gold)]"><Pencil size={14} /></button>
                    <button data-testid={`admin-${resource}-delete-${row.id}`} onClick={() => remove(row)} className="p-2 text-white/70 hover:text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length + 2} className="px-4 py-8 text-center text-white/70">Nenhum registro.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
          <div className="bg-[color:var(--marupiara-charcoal)] border border-[color:var(--marupiara-gold)]/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-white/10">
              <h2 className="font-serif text-2xl text-[color:var(--marupiara-gold)]">{editing.id ? "Editar" : "Novo"}</h2>
              <button onClick={close} className="text-white/60 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="overline text-[10px] text-white/60">{f.label}{f.required && " *"}</label>
                  {f.type === "image" ? (
                    <div className="mt-1">
                      <ImageUpload
                        value={editing[f.name] ?? ""}
                        onChange={(url) => setEditing({ ...editing, [f.name]: url })}
                        testId={`field-${f.name}`}
                      />
                    </div>
                  ) : f.type === "images" ? (
                    <div className="mt-1">
                      <MultiImageUpload
                        value={editing[f.name] ?? []}
                        onChange={(urls) => setEditing({ ...editing, [f.name]: urls })}
                        testId={`field-${f.name}`}
                      />
                    </div>
                  ) : f.type === "albums" ? (
                    <div className="mt-1">
                      <AlbumsEditor
                        value={editing[f.name] ?? []}
                        onChange={(albums) => setEditing({ ...editing, [f.name]: albums })}
                        testId={`field-${f.name}`}
                      />
                    </div>
                  ) : f.type === "color" ? (
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        type="color"
                        value={editing[f.name] || "#FFFFFF"}
                        onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                        data-testid={`field-${f.name}-picker`}
                        className="h-10 w-16 bg-transparent border border-white/10 cursor-pointer p-1"
                      />
                      <input
                        type="text"
                        value={editing[f.name] ?? ""}
                        onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                        data-testid={`field-${f.name}`}
                        placeholder="#RRGGBB (vazio = padrão)"
                        className="flex-1 bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none font-mono"
                      />
                      {editing[f.name] ? (
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, [f.name]: "" })}
                          data-testid={`field-${f.name}-clear`}
                          className="text-xs text-white/60 hover:text-[color:var(--marupiara-gold)] px-2 py-1"
                        >Limpar</button>
                      ) : null}
                    </div>
                  ) : f.type === "select" ? (
                    <select value={editing[f.name] ?? ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })} data-testid={`field-${f.name}`} className="mt-1 w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none">
                      {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "resource_select" ? (
                    <select
                      value={editing[f.name] ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        const item = (resourceOptions[f.name] || []).find((o) => o[f.valueField || "id"] === val);
                        const extras = {};
                        if (item && f.linkedFields) {
                          for (const [dst, src] of Object.entries(f.linkedFields)) {
                            extras[dst] = item[src] ?? "";
                          }
                        }
                        setEditing({ ...editing, [f.name]: val, ...extras });
                      }}
                      data-testid={`field-${f.name}`}
                      className="mt-1 w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none"
                    >
                      <option value="">— Selecione —</option>
                      {(resourceOptions[f.name] || []).map((o) => (
                        <option key={o[f.valueField || "id"]} value={o[f.valueField || "id"]}>
                          {o[f.labelField || "name"]}
                        </option>
                      ))}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <div className="flex items-center gap-2 mt-2">
                      <input type="checkbox" checked={!!editing[f.name]} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.checked })} data-testid={`field-${f.name}`} />
                      <span className="text-white/80 text-sm">{f.help || ""}</span>
                    </div>
                  ) : f.textarea ? (
                    <textarea value={editing[f.name] ?? ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })} rows={f.rows || 4} data-testid={`field-${f.name}`} className="mt-1 w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none" />
                  ) : (
                    <input type={f.type || "text"} value={editing[f.name] ?? ""} onChange={(e) => setEditing({ ...editing, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`field-${f.name}`} className="mt-1 w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none" />
                  )}
                  {f.help && f.type !== "checkbox" && f.type !== "image" && f.type !== "images" && f.type !== "albums" && <p className="text-[11px] text-white/65 mt-1">{f.help}</p>}
                </div>
              ))}
              <div>
                <label className="overline text-[10px] text-white/60">Status</label>
                <select value={editing.status ?? "published"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="field-status" className="mt-1 w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none">
                  {finalStatusOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button onClick={close} className="px-4 py-2 text-sm text-white/70 hover:text-white">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="admin-save-btn" className="btn-gold text-sm py-2 px-4"><Save size={14} /> {saving ? "Salvando…" : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
